// ─────────────────────────────────────────────────────────────────────────────
// BarChart.js — Node Usage by Region.
//
// Bars are heighted on a FIXED 0–100 scale, matching the printed Y-axis — not
// normalised to the tallest bar. A region at 40% utilisation has to look like
// 40% of the axis, or the chart reads as "this region is the busiest" when it
// only means "this region is the busiest of a quiet fleet".
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from "react";
import { ChartBody, ChartPlaceholder } from "./ChartCard";
import ChartTooltip from "./ChartTooltip";
import styles from "./BarChart.module.css";

const LABEL = "Node Usage by Region";
const Y_TICKS = [100, 75, 50, 25, 0];

/** @param {{data: {label: string, val: number}[] | null, loading: boolean}} props */
export default function BarChart({ data, loading }) {
  const [hover, setHover] = useState(null);

  if (!data || !data.length) {
    return <ChartPlaceholder label={LABEL} loading={loading || data === null} />;
  }

  return (
    <ChartBody label={LABEL}>
      <div className={styles.chart}>
        {data.map((d, i) => {
          const pct = Math.max(0, Math.min(100, d.val));
          const isHover = hover === i;
          const [first, ...rest] = d.label.split(" ");

          return (
            <div
              key={i} className={styles.col}
              onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
            >
              <div className={styles.track}>
                {isHover && (
                  // Anchored just above the top of THIS bar (bar height = pct% of
                  // the track), so the card tracks each bar instead of floating
                  // at the top of the chart.
                  <ChartTooltip
                    className={styles.barTooltip}
                    // Inline rather than in the stylesheet: it overrides
                    // ChartTooltip's own transform, and inline always wins —
                    // a class in a different CSS module would depend on which
                    // file the bundler happened to emit last.
                    style={{ bottom: `calc(${pct}% + 6px)`, left: "50%", transform: "translateX(-50%)" }}
                    title={first}
                    sub={rest.join(" ")}
                    label="Usage"
                    value={d.val}
                  />
                )}
                {/* All bars faded by default; only the hovered bar brightens. */}
                <div
                  className={styles.fill}
                  style={{ height: `${pct}%`, background: isHover ? "#00FFA3" : "rgba(0,255,163,0.12)" }}
                />
              </div>
              <span className={styles.barLabel}>{d.label}</span>
            </div>
          );
        })}
      </div>

      <div className={styles.yAxis}>
        {Y_TICKS.map((v) => <span key={v} className={styles.yLabel}>{v}</span>)}
      </div>
    </ChartBody>
  );
}
